import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl-next';
import { loadEntryPoint } from 'react-relay';

import GoalIcon from '@atlaskit/icon/core/goal';
import PageIcon from '@atlaskit/icon/core/page';
import ProjectIcon from '@atlaskit/icon/core/project';
import TeamsIcon from '@atlaskit/icon/core/teams';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { fg } from '@atlaskit/platform-feature-flags';
import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';
import { AnyAri } from '@atlassian/ari';
import type { PanelDataMap } from '@atlassian/confluence-page-preview-panel';
import { confluencePagePreviewPanelEntryPoint } from '@atlassian/confluence-page-preview-panel/entrypoint';
import atlasGoalEntryPoint from '@atlassian/goal-panel/atlas-goal-entrypoint';
import objectPreviewPanelEntryPoint from '@atlassian/object-preview-panel/object-preview-panel-entrypoint';
import { type PanelProduct, usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import atlasProjectEntryPoint from '@atlassian/project-panel/atlas-project-entrypoint';
import { getDefaultSelectedTab } from '@atlassian/projects-goals-common/tabs';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import teamPreviewPanelEntryPoint from '@atlassian/team-preview-panel/team-preview-panel-entrypoint';

import { ALL_SUPPORTED_OBJECTS } from './constants';
import messages from './messages';
import { EntryPointPreviewPanel } from './panels/EntryPointPreviewPanel';
import { IFramePreviewPanel } from './panels/IFramePreviewPanel';
import type { GlobalObjectType } from './types';
import { buildIssueViewEmbedUrl, isInIframe } from './utils';

type Product = 'jira' | 'confluence';

type Options = {
	analyticsAttributes?: AnalyticsAttributes;
	enabled?: boolean;
	rootSourceScreen?: string;
	supportedObjects?: GlobalObjectType[];
};

type IsPreviewPanelAvailable = (props: { ari: string }) => boolean;
type OpenPreviewPanel = (props: {
	ari: string;
	name: string;
	panelData?: PanelDataMap;
	parentProduct?: string;
	url: string;
}) => void;

type ReturnType = {
	isPreviewPanelAvailable: IsPreviewPanelAvailable;
	openPreviewPanel: OpenPreviewPanel;
};

const getGlobalObjectType = (ari: string): GlobalObjectType => {
	const parsedAri = AnyAri.parse(ari);
	return `${parsedAri.resourceOwner}-${parsedAri.resourceType}` as GlobalObjectType;
};

export const useGlobalObjectPreviewPanels = (
	product: Product,
	cloudId: string,
	options?: Options,
): ReturnType => {
	const {
		enabled = true,
		supportedObjects = ALL_SUPPORTED_OBJECTS,
		analyticsAttributes,
		rootSourceScreen,
	} = options ?? {};
	const environmentProvider = useRelayEnvironmentProvider();
	const { formatMessage } = useIntl();
	const { open } = usePreviewPanelsActions();

	const isPreviewPanelAvailable = useCallback(
		({ ari }: { ari: string }): boolean =>
			enabled && supportedObjects.includes(getGlobalObjectType(ari)),
		[enabled, supportedObjects],
	);

	const openPreviewPanel = useCallback(
		({
			url,
			ari,
			name,
			parentProduct,
			panelData,
		}: {
			ari: string;
			name: string;
			panelData?: PanelDataMap;
			parentProduct?: string;
			url: string;
		}): void => {
			const globalObjectType = getGlobalObjectType(ari);

			if (!isPreviewPanelAvailable({ ari })) {
				throw new Error(`Preview panel for ${globalObjectType} with ARI ${ari} is not supported.`);
			}

			// Check if we're in an iframe (not the top-most frame)
			if (isInIframe()) {
				// Post message to parent frame to open the panel
				window.parent.postMessage(
					{
						type: 'OPEN_PREVIEW_PANEL',
						payload: {
							url,
							ari,
							name,
							panelData,
						},
					},
					'*',
				);
				return;
			}

			let panelContent;
			let panelProduct: PanelProduct;
			switch (globalObjectType) {
				case 'jira-issue':
					const JiraIcon = product === 'jira' ? WorkItemIcon : WorkItemsIcon;
					const title = formatMessage(messages.workItem);

					panelContent = (
						<IFramePreviewPanel
							name={title}
							url={url}
							iframeUrl={
								fg('add_capacity_tab_to_the_board_horizontal_nav')
									? buildIssueViewEmbedUrl(url, supportedObjects, rootSourceScreen)
									: buildIssueViewEmbedUrl(url, supportedObjects)
							}
							icon={<JiraIcon label="" />}
							panelType="jira-work-item"
							product="jira"
							parentProduct={parentProduct}
							ari={ari}
							{...(fg('add_capacity_tab_to_the_board_horizontal_nav')
								? { analyticsAttributes }
								: {})}
						/>
					);
					panelProduct = 'jira';
					break;
				case 'identity-team':
					const teamEntryPointReference = loadEntryPoint<typeof teamPreviewPanelEntryPoint>(
						environmentProvider,
						teamPreviewPanelEntryPoint,
						{ cloudId },
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={teamPreviewPanelEntryPoint}
							preloadedEntryPointReference={teamEntryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								ari,
								cloudId,
								openPreviewPanel,
							}}
							globalObject={{
								name: formatMessage(messages.team),
								url: url,
								icon: <TeamsIcon label="" />,
							}}
						/>
					);
					panelProduct = 'people';
					break;
				case 'townsquare-goal':
					const entryPointReference = loadEntryPoint<typeof atlasGoalEntryPoint>(
						environmentProvider,
						atlasGoalEntryPoint,
						{ ari },
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={atlasGoalEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.goal),
								url: url,
								icon: <GoalIcon label="" />,
							}}
						/>
					);
					panelProduct = 'townsquare';
					break;
				case 'townsquare-project': {
					const entryPointReference = loadEntryPoint<typeof atlasProjectEntryPoint>(
						environmentProvider,
						atlasProjectEntryPoint,
						{ ari },
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={atlasProjectEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.project),
								url: url,
								icon: <ProjectIcon label="" />,
							}}
						/>
					);
					panelProduct = 'townsquare';
					break;
				}
				case 'confluence-page': {
					const urlObj = new URL(url);
					urlObj.searchParams.set(
						'previewPanels',
						encodeURIComponent(ALL_SUPPORTED_OBJECTS.join(',')),
					);
					const formattedUrl = urlObj.toString();
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url: formattedUrl,
								parentProduct: product,
								panelData,
								cloudId,
							}}
							globalObject={{
								name: formatMessage(messages.page),
								url: formattedUrl,
								icon: <PageIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'confluence-whiteboard': {
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
							}}
							globalObject={{
								name: formatMessage(messages.whiteboard),
								url: url,
								icon: <WhiteboardIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'cmdb-object':
					const assetsEntryPointReference = loadEntryPoint<typeof objectPreviewPanelEntryPoint>(
						environmentProvider,
						objectPreviewPanelEntryPoint,
						{ cloudId },
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={objectPreviewPanelEntryPoint}
							preloadedEntryPointReference={assetsEntryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								ari,
								cloudId,
								openPreviewPanel,
							}}
							globalObject={{
								name: formatMessage(messages.team),
								url: url,
								icon: <TeamsIcon label="" />,
							}}
						/>
					);
					panelProduct = 'assetsPreviewPanel';
					break;
				default:
					throw new Error(`Unsupported global object type: ${globalObjectType}`);
			}

			open({
				panel: {
					id: ari,
					type: globalObjectType,
					content: () => panelContent,
					product: panelProduct,
				},
			});
		},
		[
			isPreviewPanelAvailable,
			open,
			formatMessage,
			supportedObjects,
			environmentProvider,
			product,
			cloudId,
			analyticsAttributes,
			rootSourceScreen,
		],
	);

	return {
		isPreviewPanelAvailable,
		openPreviewPanel,
	};
};

export const usePreviewPanelIFrameCommunication = (openPreviewPanel: OpenPreviewPanel): void => {
	// Listen for messages from iframes to open panels
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			// Only handle messages with the expected type
			if (event.data?.type !== 'OPEN_PREVIEW_PANEL') {
				return;
			}

			const { payload } = event.data;
			if (payload?.url && payload?.ari && payload?.name) {
				openPreviewPanel({
					url: payload.url,
					ari: payload.ari,
					name: payload.name,
					panelData: payload.panelData,
				});
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [openPreviewPanel]);
};
